﻿import { NumberPortingFormProps } from "@/components/Checkout/Steps/NumberPorting/NumberPorting";
import { hasMobileAbo } from "@/hooks/useDataContextHooks";
import { Basket, BasketEntry } from "@/services/model";
import { DataContextInterface } from "@/utils/atoms";

import {
  ChargeTypeIDs,
  DEFAULT_TEXT_FIELD_REGEX,
  SIM_ALIAS_MAX_LENGTH,
} from "./constants";
import { isProductInCategory } from "./productMatrix";

export const getEntry = (dataContext: DataContextInterface) =>
  dataContext.basket?.entries["0"];

export const isValidSimAlias = (alias: string) =>
  alias.length >= 3 &&
  alias.length <= SIM_ALIAS_MAX_LENGTH &&
  DEFAULT_TEXT_FIELD_REGEX.test(alias);

export const withSimAlias = (
  basket: Basket | undefined,
  alias: string,
): Basket | undefined => {
  const entry = basket?.entries?.["0"];
  if (!basket || !entry) {
    return basket;
  }

  return {
    ...basket,
    entries: {
      ...basket.entries,
      "0": {
        ...entry,
        customData: {
          ...entry.customData,
          SimAlias: alias,
        },
      },
    },
  };
};

/**
 * Basket refreshes (getUpdatedBasket / setDevice) can drop a locally prefilled
 * SimAlias that hasn't been persisted yet. Keep the previous alias when the
 * incoming basket is missing one.
 */
export const withPreservedSimAlias = (
  previousBasket: Basket | undefined,
  nextBasket: Basket,
): Basket => {
  const previousAlias = previousBasket?.entries?.["0"]?.customData?.[
    "SimAlias"
  ] as string | undefined;
  const nextEntry = nextBasket.entries?.["0"];
  const nextAlias = nextEntry?.customData?.["SimAlias"] as string | undefined;

  if (!previousAlias || nextAlias || !nextEntry) {
    return nextBasket;
  }

  return withSimAlias(nextBasket, previousAlias) ?? nextBasket;
};
/**
 * Whether number porting is active for checkout display.
 * The backend may persist mnpData on telcoData while entry.onp stays false.
 */
export const hasBasketNumberPorting = (
  entry: BasketEntry | undefined,
  numberPorting?: NumberPortingFormProps,
): boolean => {
  if (numberPorting?.enableNumberPorting === "no") {
    return false;
  }

  if (numberPorting?.enableNumberPorting === "yes") {
    return true;
  }

  if (entry?.telcoData?.mnpData?.msisdn) {
    return true;
  }

  return !!entry?.onp;
};

export const hasBasketEntryWithVoiceOption = (
  dataContext: DataContextInterface,
) => {
  const basketEntry = getEntry(dataContext);
  const oneTimeCharges = basketEntry?.options?.[0]?.oneTimeCharges;
  const product = basketEntry?.options?.[0]?.product;

  return (
    oneTimeCharges?.some(
      (charge) => charge.chargeType?.id === ChargeTypeIDs.VoiceOption,
    ) ||
    (isProductInCategory(product?.id, "mobile") && !hasMobileAbo(dataContext))
  );
};
